import { useCallback, useState, useEffect } from 'react';
import { useBlocker, useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { TextField, Checkbox } from '../../../components/inputs';
import { validateSchemaAndNotifyErrors } from '../../../utils/validationUtils';
import { useTRPCRequest } from '../../../hooks/useTRPCRequest';
import { TRPCMethodEnum, TRPCResourceEnum } from '../../../api/trpcApi/types';
import {
  BackButtonComponent,
  SaveButtonComponent,
  ContainedButton
} from '../../../components/buttons';
import {
  ConfirmDeleteModalComponent,
  LoadingModalComponent,
  UnsavedChangesModalComponent,
  ModalComponent
} from '../../../components/modals';
import { Title } from '../../../components/Title';
import { SearchBar } from '../../../components/search-bar';
import { useProducts } from '../../products/hooks/useProducts';
import { Product } from '../../products/types';
import { PartnerProduct } from '../../partnerProducts/types';

import { PARTNER_FIELDS, Partner, PartnerErrors } from '../types';
import PartnerValidation from '../validations';

const inputStyles = 'mb-5 grow text-base font-semibold';

interface PartnerDetailsProps {
  create?: boolean;
}

export function PartnerDetails({ create = false }: PartnerDetailsProps) {
  const navigate = useNavigate();
  const params = useParams();
  const { handleTRPCRequest } = useTRPCRequest();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const unsavedChangesBlocker = useBlocker(unsavedChanges);

  const [partner, setPartner] = useState<Partner>({
    name: '',
    system_name: '',
    refersion_id: '',
    active: true
  });
  const [formErrors, setFormErrors] = useState<PartnerErrors>({
    name: false,
    system_name: false,
    refersion_id: false,
    active: false
  });

  // Partner Products state
  const [partnerProducts, setPartnerProducts] = useState<PartnerProduct[]>([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [confirmRemoveProduct, setConfirmRemoveProduct] = useState<
    string | null
  >(null);
  const {
    products,
    isLoading: isLoadingProducts,
    refetch: refetchProducts
  } = useProducts();

  const syncFromApi = useCallback(async () => {
    setIsLoading(true);
    if (params.partner_id) {
      const { res: fetchedPartner, error } = await handleTRPCRequest({
        method: TRPCMethodEnum.get,
        resourceType: TRPCResourceEnum.partners,
        requestBody: {
          partner_id: params.partner_id
        }
      });

      if (!error) {
        setPartner(fetchedPartner);
      }

      // Fetch partner products
      const { res: fetchedProducts, error: productsError } =
        await handleTRPCRequest({
          method: TRPCMethodEnum.listByPartner,
          resourceType: TRPCResourceEnum.partnerProducts,
          requestBody: {
            partner_id: params.partner_id
          }
        });

      if (!productsError && fetchedProducts) {
        setPartnerProducts(fetchedProducts);
      }
    }

    setIsLoading(false);
  }, [params.partner_id, handleTRPCRequest]);

  useEffect(() => {
    if (create) {
      setIsLoading(false);
    } else {
      syncFromApi();
    }
    setUnsavedChanges(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [create]);

  const handleUpdatePartner = useCallback(
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setPartner(oldPartner => {
        const newPartner = {
          ...oldPartner,
          [key]: event.target.value
        };
        return newPartner;
      });
      setFormErrors({ ...formErrors, [key]: null });
      setUnsavedChanges(true);
    },
    [formErrors, setPartner, setFormErrors]
  );

  const handleToggleActive = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPartner(oldPartner => ({
        ...oldPartner,
        active: event.target.checked
      }));
      setUnsavedChanges(true);
    },
    []
  );

  // Auto-generate system_name from name
  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newName = event.target.value;
      setPartner(oldPartner => {
        const newPartner = {
          ...oldPartner,
          name: newName
        };
        // Only auto-generate system_name if it's empty or matches the previous auto-generated value
        if (
          create &&
          (oldPartner.system_name === '' ||
            oldPartner.system_name === generateSystemName(oldPartner.name))
        ) {
          newPartner.system_name = generateSystemName(newName);
        }
        return newPartner;
      });
      setFormErrors({ ...formErrors, name: false });
      setUnsavedChanges(true);
    },
    [create, formErrors]
  );

  const generateSystemName = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Create a set of assigned product IDs for quick lookup
  const assignedProductIds = new Set(partnerProducts.map(pp => pp.product_id));

  // Add isAssigned flag to each product
  const productsWithAssignmentStatus = products.map((product: Product) => ({
    ...product,
    isAssigned: product.product_id
      ? assignedProductIds.has(product.product_id)
      : false
  }));

  // Filter by search term but keep all products (assigned and unassigned)
  const filteredProducts = productSearch
    ? productsWithAssignmentStatus.filter(
        product =>
          product.system_name
            ?.toLowerCase()
            .includes(productSearch.toLowerCase())
      )
    : productsWithAssignmentStatus;

  const handleAddProduct = useCallback(
    async (product: Product) => {
      if (!params.partner_id || !product.product_id) return;

      setIsAddingProduct(true);
      const { res, error } = await handleTRPCRequest({
        method: TRPCMethodEnum.create,
        resourceType: TRPCResourceEnum.partnerProducts,
        requestBody: {
          partner_id: params.partner_id,
          product_id: product.product_id
        }
      });

      if (!error && res) {
        setPartnerProducts(prev => [
          ...prev,
          { ...res, product_system_name: product.system_name }
        ]);
        toast.success(`Product "${product.system_name}" added successfully!`);
      }
      setIsAddingProduct(false);
      setIsProductModalOpen(false);
      setProductSearch('');
    },
    [params.partner_id, handleTRPCRequest]
  );

  const handleRemoveProduct = useCallback(
    async (partnerProductId: string) => {
      const { error } = await handleTRPCRequest({
        method: TRPCMethodEnum.delete,
        resourceType: TRPCResourceEnum.partnerProducts,
        requestBody: {
          partner_product_id: partnerProductId
        }
      });

      if (!error) {
        setPartnerProducts(prev =>
          prev.filter(pp => pp.partner_product_id !== partnerProductId)
        );
        toast.success('Product removed successfully!');
      }
      setConfirmRemoveProduct(null);
    },
    [handleTRPCRequest]
  );

  const handleOpenProductModal = useCallback(() => {
    setIsProductModalOpen(true);
    refetchProducts();
  }, [refetchProducts]);

  const handleSavePartner = useCallback(async () => {
    setIsSaving(true);

    const partnerToSave = { ...partner };
    if (create) {
      delete partnerToSave.partner_id;
    }

    const { errors: validationErrors, validatedSchema } =
      validateSchemaAndNotifyErrors(
        create
          ? PartnerValidation.createPartnerSchema
          : PartnerValidation.updatePartnerSchema,
        partnerToSave
      );

    if (validationErrors) {
      setFormErrors(validationErrors as unknown as PartnerErrors);
    } else {
      const { res, error } = await handleTRPCRequest({
        method: create ? TRPCMethodEnum.create : TRPCMethodEnum.update,
        resourceType: TRPCResourceEnum.partners,
        requestBody: validatedSchema
      });

      if (!error) {
        setFormErrors({
          name: false,
          system_name: false,
          refersion_id: false,
          active: false
        });
        toast.success(
          create
            ? 'Partner created successfully!'
            : 'Partner saved successfully!'
        );
        navigate(`/partners/${res?.partner_id}`, { replace: true });
      }
      setUnsavedChanges(false);
    }
    setIsSaving(false);
  }, [create, partner, setFormErrors, handleTRPCRequest, navigate]);

  const handleDeletePartner = useCallback(async () => {
    setUnsavedChanges(false);
    setConfirmDelete(false);
    setIsDeleting(true);
    if (partner.partner_id) {
      const { error } = await handleTRPCRequest({
        method: TRPCMethodEnum.delete,
        resourceType: TRPCResourceEnum.partners,
        requestBody: { partner_id: partner.partner_id }
      });
      if (!error) {
        toast.success('Partner deleted successfully!');
        navigate('/partners', { replace: true });
      }

      setIsDeleting(false);
    }
  }, [partner.partner_id, navigate, handleTRPCRequest]);

  if (isSaving) {
    return <LoadingModalComponent isOpen={isSaving} message="Saving..." />;
  }

  if (isDeleting) {
    return <LoadingModalComponent isOpen={isDeleting} message="Deleting..." />;
  }

  if (isLoading) {
    return <LoadingModalComponent isOpen={isLoading} />;
  }

  return (
    <div className="flex flex-col gap-3">
      {create ? (
        <Title title="Create Partner" />
      ) : (
        <Title title="Edit Partner" />
      )}

      <div className="card mb-5 p-5">
        <h2 className="mb-4 text-lg font-semibold">Basic Information</h2>
        <div className="flex w-full space-x-2.5">
          <TextField
            error={formErrors.name}
            contentBefore="Name:"
            size="small"
            className={inputStyles}
            value={partner.name}
            onChange={handleNameChange}
            data-testid={PARTNER_FIELDS.name}
          />
          <TextField
            error={formErrors.system_name}
            contentBefore="System Name:"
            size="small"
            className={inputStyles}
            value={partner.system_name}
            onChange={handleUpdatePartner(PARTNER_FIELDS.system_name)}
            data-testid={PARTNER_FIELDS.system_name}
          />
        </div>
        <div className="flex w-full space-x-2.5">
          <TextField
            error={formErrors.refersion_id}
            contentBefore="Refersion ID:"
            size="small"
            className={inputStyles}
            value={partner.refersion_id}
            onChange={handleUpdatePartner(PARTNER_FIELDS.refersion_id)}
            data-testid={PARTNER_FIELDS.refersion_id}
          />
          {!create && (
            <div className={`${inputStyles} flex items-center`}>
              <Checkbox
                label="Active"
                checked={partner.active}
                onChange={handleToggleActive}
                data-testid={PARTNER_FIELDS.active}
              />
            </div>
          )}
        </div>
      </div>

      {/* Assigned Products Section - only show when editing */}
      {!create && (
        <div className="card mb-5 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Assigned Products ({partnerProducts.length})
            </h2>
            <ContainedButton onClick={handleOpenProductModal}>
              Add Product
            </ContainedButton>
          </div>

          {partnerProducts.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              No products assigned to this partner.
              <br />
              <button
                className="mt-2 text-blue-500 hover:underline"
                onClick={handleOpenProductModal}
              >
                Add a product
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-semibold">Product</th>
                  <th className="pb-2 font-semibold">Customized</th>
                  <th className="pb-2 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partnerProducts.map(pp => (
                  <tr
                    key={pp.partner_product_id}
                    className="border-b last:border-b-0"
                  >
                    <td className="py-3">
                      <Link
                        to={`/partners/${params.partner_id}/products/${pp.product_id}`}
                        className="text-blue-500 hover:underline"
                      >
                        {pp.product_system_name || pp.product_id}
                      </Link>
                    </td>
                    <td className="py-3">
                      {(pp.style_count ?? 0) > 0 ? (
                        <span className="text-turquoise text-sm">
                          ✓ {pp.style_count} style
                          {(pp.style_count ?? 0) !== 1 ? 's' : ''} selected
                        </span>
                      ) : (
                        <span className="text-light-gray text-sm italic">
                          Using defaults
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      <Link
                        to={`/partners/${params.partner_id}/products/${pp.product_id}`}
                        className="mr-3 text-blue-500 hover:underline"
                      >
                        Configure
                      </Link>
                      <button
                        className="text-red-500 hover:underline"
                        onClick={() =>
                          setConfirmRemoveProduct(pp.partner_product_id)
                        }
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="grow" />

      <div className="mt-10 flex gap-3">
        <BackButtonComponent destination="/partners" />
        {!create && (
          <button
            className={`rounded px-4 py-2 text-white ${
              partnerProducts.length > 0
                ? 'cursor-not-allowed bg-gray-400'
                : 'bg-red-500 hover:bg-red-600'
            }`}
            onClick={() => setConfirmDelete(true)}
            disabled={partnerProducts.length > 0}
            title={
              partnerProducts.length > 0
                ? 'Remove all products before deleting partner'
                : undefined
            }
          >
            Delete
          </button>
        )}
        <SaveButtonComponent isSaving={isSaving} onSave={handleSavePartner} />
      </div>
      <UnsavedChangesModalComponent
        unsavedChanges={unsavedChanges}
        unsavedChangesBlocker={unsavedChangesBlocker}
      />
      <ConfirmDeleteModalComponent
        isOpen={confirmDelete}
        entityName={partner.name}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDeletePartner}
      />

      {/* Product Selection Modal */}
      <ModalComponent
        isOpen={isProductModalOpen}
        onRequestClose={() => {
          setIsProductModalOpen(false);
          setProductSearch('');
        }}
        contentLabel="Add Product"
        className="w-[600px]"
      >
        <h2 className="mb-4 text-lg font-semibold">Add Product</h2>
        <SearchBar
          placeHolder="Search products..."
          searchTerm={productSearch}
          onSearch={setProductSearch}
          searchOnChange
          disabled={isLoadingProducts}
        />
        <div className="mt-4 h-96 overflow-y-auto">
          {isLoadingProducts ? (
            <div className="py-4 text-center text-gray-500">Loading...</div>
          ) : filteredProducts.length === 0 ? (
            <div className="py-4 text-center text-gray-500">
              {productSearch
                ? 'No products found matching your search.'
                : 'No products available.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredProducts.map(product => (
                <div
                  key={product.product_id}
                  className={`flex w-full items-center justify-between rounded border p-3 text-left ${
                    product.isAssigned
                      ? 'cursor-not-allowed bg-gray-100 opacity-60'
                      : 'cursor-pointer hover:bg-gray-50'
                  }`}
                  onClick={() =>
                    !product.isAssigned &&
                    !isAddingProduct &&
                    handleAddProduct(product)
                  }
                >
                  <span
                    className={product.isAssigned ? 'text-gray-500' : undefined}
                  >
                    {product.system_name}
                  </span>
                  {product.isAssigned && (
                    <span className="text-sm italic text-gray-500">
                      Already assigned
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <ContainedButton
            color="secondary"
            onClick={() => {
              setIsProductModalOpen(false);
              setProductSearch('');
            }}
          >
            Cancel
          </ContainedButton>
        </div>
      </ModalComponent>

      {/* Confirm Remove Product Modal */}
      <ConfirmDeleteModalComponent
        isOpen={!!confirmRemoveProduct}
        entityName="this product from the partner"
        onCancel={() => setConfirmRemoveProduct(null)}
        onConfirm={() =>
          confirmRemoveProduct && handleRemoveProduct(confirmRemoveProduct)
        }
      />
    </div>
  );
}
